import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Input, Textarea, Field, Spinner, Modal, Select } from '../ui.jsx';

// Schwartz-style angle library — proven hooks adapted for trades businesses
const ANGLE_LIBRARY = [
  {
    angle: 'Pain', emoji: '💢',
    description: 'Lead with the felt pain the customer is experiencing right now.',
    hooks: [
      'Sweating it out without AC? You\'re not alone — half of Tampa is right there with you.',
      'That drip you\'ve been ignoring for two weeks is about to become a flood.',
      'Walking past that cracked tile every day, hoping it fixes itself?',
      'No hot water for the third morning in a row. That\'s a hard way to start a day.',
    ],
    body: [
      'Look — the problem isn\'t going away on its own. Most people wait until it\'s catastrophic, then pay 3x for an emergency call. Here\'s the better play: 30-minute diagnostic, $89 (waived if we do the repair). Same-day, this week. We\'ve got 3 slots left.',
    ],
  },
  {
    angle: 'Urgency / Scarcity', emoji: '⏰',
    description: 'Create real urgency without manufactured scarcity.',
    hooks: [
      'Tampa heat\'s coming — last call for spring tune-ups before we book solid in May.',
      '3 weeks until the rainy season. Get the roof inspected before water finds the cracks.',
      'Winter\'s 60 days out. Heating tune-ups slots are going fast.',
    ],
    body: [
      'We\'re booking 2 weeks out right now. Once we\'re full, the next opening is 4-6 weeks. If your system\'s 10+ years old, get on the schedule today.',
    ],
  },
  {
    angle: 'Trust / Social Proof', emoji: '🤝',
    description: 'Lead with authority + customer evidence.',
    hooks: [
      'Why 400+ Tampa families chose us this year.',
      '15 years, zero complaints with the BBB. Here\'s what changed our business.',
      '"Saved me $4,200 vs the replacement quote." — Sarah, Brandon.',
    ],
    body: [
      'We\'re not the cheapest. We\'re the ones you call when you don\'t want to do this twice. Licensed, insured, EPA-certified. Same crew shows up every time. Read 87 verified 5-star reviews → ',
    ],
  },
  {
    angle: 'Cost of Inaction', emoji: '💸',
    description: 'Make the cost of doing nothing visceral.',
    hooks: [
      'Skip the tune-up this year, pay for the compressor next.',
      'That leak adds $40-80 to your water bill every month. Just sitting there.',
      'Most "small" electrical issues are 60 days away from being a fire risk.',
    ],
    body: [
      'A $129 maintenance visit catches what a $4,800 replacement misses. Math\'s simple. We schedule, we show up on time, you stop worrying about it.',
    ],
  },
  {
    angle: 'Offer / Discount', emoji: '🎁',
    description: 'Clear discount with a deadline + qualifier.',
    hooks: [
      'May only: $89 AC tune-up (normally $149). Includes 20-point inspection.',
      'New customers: free second opinion on any HVAC quote. We\'ll tell you straight.',
      'Mention this ad: $50 off any service call this week.',
    ],
    body: [
      'No fine print. No upsells you didn\'t ask for. Book online or text us. Limited slots, ends Sunday.',
    ],
  },
  {
    angle: 'Founder Story', emoji: '👋',
    description: 'Personal story → why your business is different.',
    hooks: [
      'I started this company because I got tired of customers calling and saying "the last guy ripped me off."',
      '12 years in HVAC, 4 with my own truck. Here\'s the thing nobody tells you.',
      'When my mom\'s AC died in July, the company she called charged her $5,200 for a unit that retails at $2,800. That\'s why I do what I do.',
    ],
    body: [
      'No commissioned salespeople. No "your warranty\'s expired" upcharges. Flat-rate, transparent, on-time. The way it should\'ve been all along.',
    ],
  },
];

const PLATFORM_GUIDE = {
  facebook_page:  { label:'📘 Facebook',  charLimit: 8000,  best: 'Story-driven, conversational, 100-200 words. 1-2 hashtags.' },
  instagram_business: { label:'📷 Instagram', charLimit: 2200, best: 'Hook in first line. Line breaks between sentences. 5-8 niche hashtags at end.' },
  tiktok_organic: { label:'🎵 TikTok',    charLimit: 2200, best: 'Punchy hook. Under 150 chars in caption. Trending hashtags.' },
  youtube:        { label:'▶️ YouTube',   charLimit: 5000, best: 'SEO keywords first. Detailed description with timestamps if long video.' },
  gbp:            { label:'📍 Google Biz', charLimit: 1500, best: 'Local-business tone. Mention service area. CTA to call or book.' },
};

export function ContentStudio({ user, toast }) {
  const [tab, setTab] = useState('compose');
  return <div style={{ padding:24, maxWidth:1100 }}>
    <h1 style={{ margin:'0 0 14px 0', fontSize:24, fontWeight:800 }}>📝 Content Studio</h1>
    <div style={{ display:'flex', gap:4, marginBottom:18, borderBottom:`1px solid ${G.cardBorder}`, overflowX:'auto' }}>
      {[
        { k:'compose',   label:'✍️ Compose + Queue' },
        { k:'images',    label:'🎨 Image Generator' },
        { k:'angles',    label:'🎯 Angles & Hooks' },
        { k:'recurring', label:'🤖 AI Drip' },
        { k:'history',   label:'📋 Posted' },
      ].map(t => (
        <button key={t.k} onClick={()=>setTab(t.k)}
          style={{ background:'none', border:'none', color: tab===t.k?G.purple:G.textMuted,
            padding:'10px 16px', fontWeight: tab===t.k?700:500, cursor:'pointer', whiteSpace:'nowrap',
            borderBottom: tab===t.k?`2px solid ${G.purple}`:'2px solid transparent', marginBottom:-1 }}>
          {t.label}
        </button>
      ))}
    </div>
    {tab === 'compose'   && <ComposeWithQueue toast={toast}/>}
    {tab === 'images'    && <ImageGenTab toast={toast}/>}
    {tab === 'angles'    && <AnglesTab toast={toast}/>}
    {tab === 'recurring' && <RecurringTab toast={toast}/>}
    {tab === 'history'   && <HistoryTab toast={toast}/>}
  </div>;
}

// 🎨 Image Generator — AI image gen for marketing. Calls OpenAI gpt-image-1
// via /api/marketing/generate-image, which saves the PNG to /uploads and
// returns the public URL. Square / portrait / landscape covers IG / Stories / FB cover.
const FORMATS = [
  { key:'square',    label:'Square 1:1',     dim:'1024×1024', use:'IG feed · FB feed · GBP' },
  { key:'portrait',  label:'Portrait 2:3',   dim:'1024×1536', use:'Stories · Reels cover · Pinterest' },
  { key:'landscape', label:'Landscape 3:2',  dim:'1536×1024', use:'FB cover · banner · YT thumb' },
];
const QUALITIES = [
  { key:'low',    label:'Draft',     cost:'~$0.01' },
  { key:'medium', label:'Standard',  cost:'~$0.04' },
  { key:'high',   label:'Print',     cost:'~$0.17' },
];
const STARTER_PROMPTS = [
  'iPhone photo of a friendly HVAC tech holding a service tablet in front of a homeowner\'s front door, natural daylight, eye-level, authentic, unstaged',
  'A real plumber kneeling next to a kitchen sink, fixing a leak, golden hour window light, slight low angle, authentic phone photo, no studio polish',
  'Cracked outdoor AC condenser with rust spots on a Florida driveway, harsh midday sun, iPhone macro shot, real environment, no people',
  'Before/after panel upgrade: dark messy fuse box on left, clean modern 200A breaker panel on right, garage interior, natural light',
];

function ImageGenTab({ toast }) {
  const [prompt, setPrompt] = useState('');
  const [format, setFormat] = useState('square');
  const [quality, setQuality] = useState('medium');
  const [n, setN] = useState(1);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tradeops-images-history') || '[]'); }
    catch { return []; }
  });

  useEffect(() => {
    try { localStorage.setItem('tradeops-images-history', JSON.stringify(history.slice(0, 40))); } catch {}
  }, [history]);

  const generate = async () => {
    if (prompt.trim().length < 3) { toast('Prompt too short', 'error'); return; }
    setBusy(true);
    try {
      const r = await api('/api/marketing/generate-image', {
        method: 'POST',
        body: { prompt: prompt.trim(), format, quality, n },
      });
      const entries = (r.images || []).map(img => ({ ...img, prompt: prompt.trim(), at: Date.now() }));
      setHistory(h => [...entries, ...h]);
      toast(`✓ Generated ${entries.length} image${entries.length>1?'s':''}`);
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = (url) => {
    try { navigator.clipboard.writeText(url); toast('URL copied'); }
    catch { window.prompt('Image URL:', url); }
  };

  return <div>
    <div style={{ ...styles.card, padding: 18, marginBottom: 18 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: G.textMuted, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Prompt</div>
      <textarea
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        placeholder="Describe the image you want. Be specific: subject, environment, camera angle, lighting, mood. Example: 'iPhone photo of an HVAC tech smiling on a Florida driveway, natural daylight, authentic, unstaged.'"
        style={{
          width: '100%',
          minHeight: 90,
          padding: '12px 14px',
          background: G.input,
          border: `1px solid ${G.cardBorder}`,
          borderRadius: 8,
          color: G.text,
          fontSize: 14,
          fontFamily: 'inherit',
          resize: 'vertical',
          outline: 'none',
          marginBottom: 10,
        }}
      />

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 11, color: G.textMuted, marginRight: 6, alignSelf: 'center', fontWeight: 600 }}>Starters:</div>
        {STARTER_PROMPTS.map((p, i) => (
          <div key={i} onClick={() => setPrompt(p)} style={{
            fontSize: 11, padding: '4px 10px', borderRadius: 12,
            background: G.purpleDim, color: G.purple, cursor: 'pointer', whiteSpace: 'nowrap',
            maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis',
          }} title={p}>{p.slice(0, 36)}…</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 11, color: G.textMuted, fontWeight: 600, marginBottom: 4 }}>FORMAT</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {FORMATS.map(f => (
              <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 8px', borderRadius: 6, background: format === f.key ? G.purpleDim : 'transparent' }}>
                <input type="radio" checked={format === f.key} onChange={() => setFormat(f.key)} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{f.label} <span style={{ color: G.textMuted, fontWeight: 400 }}>· {f.dim}</span></div>
                  <div style={{ fontSize: 11, color: G.textMuted }}>{f.use}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, color: G.textMuted, fontWeight: 600, marginBottom: 4 }}>QUALITY</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {QUALITIES.map(q => (
              <label key={q.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 8px', borderRadius: 6, background: quality === q.key ? G.purpleDim : 'transparent' }}>
                <input type="radio" checked={quality === q.key} onChange={() => setQuality(q.key)} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{q.label} <span style={{ color: G.textMuted, fontWeight: 400 }}>· {q.cost}</span></div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, color: G.textMuted, fontWeight: 600, marginBottom: 4 }}>VARIATIONS</div>
          <select value={n} onChange={e => setN(Number(e.target.value))} style={{ width: '100%', padding: '10px 12px', background: G.input, border: `1px solid ${G.cardBorder}`, borderRadius: 6, color: G.text, fontSize: 14 }}>
            <option value={1}>1 image</option>
            <option value={2}>2 variations</option>
            <option value={3}>3 variations</option>
            <option value={4}>4 variations</option>
          </select>
        </div>
      </div>

      <Btn v="primary" sz="lg" onClick={generate} disabled={busy || prompt.trim().length < 3}>
        {busy ? <><Spinner sz={14}/> Generating…</> : '🎨 Generate'}
      </Btn>
    </div>

    {history.length > 0 && (
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: G.textMuted, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>
          Recent generations ({history.length})
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {history.map((img, i) => (
            <div key={i} style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
              <img src={img.url} alt={img.prompt} style={{ width: '100%', display: 'block', aspectRatio: img.format === 'square' ? '1 / 1' : img.format === 'portrait' ? '2 / 3' : '3 / 2', objectFit: 'cover' }}/>
              <div style={{ padding: '10px 12px' }}>
                <div style={{ fontSize: 11, color: G.textMuted, marginBottom: 6, lineHeight: 1.4, height: 30, overflow: 'hidden' }} title={img.prompt}>
                  {img.prompt.slice(0, 80)}{img.prompt.length > 80 ? '…' : ''}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn sz="xs" onClick={() => copyUrl(img.url)}>📋 Copy URL</Btn>
                  <Btn sz="xs" onClick={() => window.open(img.url, '_blank')}>↗ Open</Btn>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>;
}

// ComposeWithQueue — merges the approval inbox into the top of the Compose
// view. Pending AI-drip + ad-creative drafts surface here so the owner can
// approve, edit, or kill in one place.
function ComposeWithQueue({ toast }) {
  const [pending, setPending] = useState(null);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const loadPending = () => api('/api/social/scheduled')
    .then(rows => {
      // Endpoint can return either an array or { scheduled: [...] } depending on
      // version. Tolerate both, default to [].
      const arr = Array.isArray(rows) ? rows : Array.isArray(rows?.scheduled) ? rows.scheduled : [];
      setPending(arr.filter(r => r && r.status === 'pending'));
    })
    .catch(() => setPending([]));
  // Wrap in inline arrow — passing `loadPending` directly causes React to
  // store its returned Promise as the cleanup function, which it later calls
  // as `cleanup()` → "is not a function" crash that blanks the whole tab.
  useEffect(() => { loadPending(); }, []);

  const approve = async (id) => { try { await api(`/api/social/scheduled/${id}/approve`, { method:'POST' }); loadPending(); toast('✓ Approved'); } catch(e) { toast(e.message,'error'); } };
  const remove  = async (id) => { try { await api(`/api/social/scheduled/${id}`, { method:'DELETE' }); loadPending(); toast('Deleted'); } catch(e) { toast(e.message,'error'); } };

  const needsApproval = (pending||[]).filter(i => i.needsApproval);

  return <div>
    {/* Approval queue rolled into the same tab as composing */}
    {needsApproval.length > 0 && <div style={{ ...styles.card, padding:14, marginBottom:18, borderColor: G.yellow+'66', background: G.yellow+'11' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
        <strong style={{ fontSize:14, color: G.yellow }}>🤖 Awaiting your approval ({needsApproval.length})</strong>
        <div style={{ fontSize:11, color:G.textMuted }}>AI-drip drafts. Approve to publish at scheduled time.</div>
      </div>
      {needsApproval.slice(0, 5).map(i => (
        <div key={i.id} style={{ ...styles.card, padding:12, marginBottom:8 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:14 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, marginBottom:4 }}>
                {new Date(i.scheduledFor).toLocaleString()} · {i.platforms.join(', ')}
              </div>
              <div style={{ fontSize:13, whiteSpace:'pre-wrap', maxHeight:80, overflow:'hidden' }}>{i.caption}</div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              <Btn sz="xs" v="primary" onClick={()=>approve(i.id)}>✓ Approve</Btn>
              <Btn sz="xs" v="danger" onClick={()=>remove(i.id)}>× Reject</Btn>
            </div>
          </div>
        </div>
      ))}
    </div>}

    {/* Campaign builder shortcut */}
    <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
      <Btn v="primary" onClick={()=>setCampaignOpen(true)}>📣 Build Campaign</Btn>
    </div>

    {/* The actual composer */}
    <ComposeTab toast={toast}/>

    {campaignOpen && <CampaignWizardModal onClose={()=>setCampaignOpen(false)} onDone={()=>{ setCampaignOpen(false); loadPending(); toast('✓ Campaign + creatives generated'); }} toast={toast}/>}
  </div>;
}

function CampaignWizardModal({ onClose, onDone, toast }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({
    objective: 'leads',
    platform: 'meta_ads',
    audience: '',
    budgetCents: 5000,    // $50/day default
    angle: '',
    count: 3,
  });
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      // 1. Create the campaign
      const camp = await api('/api/marketing/campaigns', { method:'POST', body:{
        name: `${f.objective.toUpperCase()} — ${f.audience || 'Local homeowners'}`,
        platform: f.platform,
        budget_cents: f.budgetCents,
        status: 'draft',
      }});
      // 2. Generate creatives (Schwartz/Ogilvy + UGC visuals)
      await api(`/api/marketing/campaigns/${camp.id}/generate-creatives`, { method:'POST', body:{
        count: f.count, kind:'mixed',
        hook: `${f.objective} for ${f.audience || 'homeowners'}${f.angle ? ` — angle: ${f.angle}` : ''}`,
      }});
      onDone();
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return <Modal open onClose={onClose} width={580}>
    <h2 style={{ margin:'0 0 6px', fontSize:18 }}>📣 Build Campaign</h2>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>
      We'll generate {f.count} Schwartz/Ogilvy ad creatives with native UGC visuals. Each one tests a different angle. Approved creatives push to {f.platform === 'meta_ads' ? 'Meta as PAUSED ads' : f.platform}.
    </div>

    <Field label="Objective">
      <Select value={f.objective} onChange={e=>setF({...f, objective:e.target.value})}>
        <option value="leads">📞 Leads — get the phone to ring</option>
        <option value="bookings">📅 Bookings — fill the calendar</option>
        <option value="awareness">👀 Awareness — build top-of-mind</option>
        <option value="memberships">⭐ Memberships — sell maintenance plans</option>
      </Select>
    </Field>

    <Field label="Platform">
      <Select value={f.platform} onChange={e=>setF({...f, platform:e.target.value})}>
        <option value="meta_ads">Meta (Facebook + Instagram)</option>
        <option value="google_ads">Google Ads</option>
        <option value="tiktok_ads">TikTok Ads</option>
        <option value="google_lsa">Google Local Service Ads</option>
      </Select>
    </Field>

    <Field label="Audience / market" hint="City + service type. The AI uses this to make copy specific.">
      <Input value={f.audience} onChange={e=>setF({...f, audience:e.target.value})}
        placeholder="e.g. 'Tampa homeowners with old AC units'"/>
    </Field>

    <Field label="Angle (optional)" hint="Specific hook to test. Leave blank to let the AI rotate angles.">
      <Input value={f.angle} onChange={e=>setF({...f, angle:e.target.value})}
        placeholder="e.g. 'Spring tune-up before May rush'"/>
    </Field>

    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Daily budget">
        <Input type="number" value={f.budgetCents/100} onChange={e=>setF({...f, budgetCents: Math.round(Number(e.target.value)*100)})}/>
      </Field>
      <Field label="# of creatives to generate">
        <Select value={f.count} onChange={e=>setF({...f, count:Number(e.target.value)})}>
          <option value={3}>3 creatives (recommended)</option>
          <option value={5}>5 creatives (max test surface)</option>
          <option value={1}>1 creative (quick test)</option>
        </Select>
      </Field>
    </div>

    <div style={{ ...styles.card, padding:12, marginTop:14, background:G.input, fontSize:12, color:G.textMuted, lineHeight:1.5 }}>
      <strong style={{ color:G.text }}>Best practice we apply automatically:</strong>
      <ul style={{ paddingLeft:18, margin:'6px 0 0' }}>
        <li>Each creative uses a DIFFERENT Schwartz angle (Pain / Urgency / Trust / Cost of Inaction / Offer / Founder Story)</li>
        <li>Body follows 6-segment lock — Hook → Qualifier → Pain → Cost of Inaction → Solution → CTA</li>
        <li>No em dashes, no corporate jargon, single CTA per ad</li>
        <li>Visuals shot phone-in-hand style — iPhone photo, natural light, unstaged</li>
        <li>Mixed image + video to give the algorithm room to test</li>
      </ul>
    </div>

    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
      <Btn v="primary" onClick={generate} disabled={busy || !f.audience.trim()}>{busy ? <Spinner sz={14}/> : `Generate ${f.count} creatives`}</Btn>
    </div>
  </Modal>;
}

function ComposeTab({ toast }) {
  const [mode, setMode] = useState('organic'); // 'organic' | 'ad'
  const [caption, setCaption] = useState('');
  const [media, setMedia] = useState(null); // { url, type: 'image'|'video' }
  const [selected, setSelected] = useState({});
  const [status, setStatus] = useState(null);
  const [posting, setPosting] = useState(false);
  const [results, setResults] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [scope, setScope] = useState('');
  const [genBusy, setGenBusy] = useState(false);
  const [videoTier, setVideoTier] = useState('standard');
  const [videoDuration, setVideoDuration] = useState(6); // seconds; >30 routes to long-form pipeline
  const [scheduleAt, setScheduleAt] = useState('');
  const [billingWarning, setBillingWarning] = useState(null);

  useEffect(() => { api('/api/social/status').then(setStatus).catch(()=>{}); }, []);

  const platforms = [
    { key:'facebook_page',      ...PLATFORM_GUIDE.facebook_page,      available: status?.facebook?.connected },
    { key:'instagram_business', ...PLATFORM_GUIDE.instagram_business, available: status?.instagram?.connected },
    { key:'youtube',            ...PLATFORM_GUIDE.youtube,            available: status?.youtube?.connected, requiresVideo: true },
    { key:'tiktok_organic',     ...PLATFORM_GUIDE.tiktok_organic,     available: status?.tiktok?.connected, requiresVideo: true },
    { key:'gbp',                ...PLATFORM_GUIDE.gbp,                available: status?.gbp?.connected },
  ];

  const toggle = (k) => setSelected(s => ({ ...s, [k]: !s[k] }));
  const selectedKeys = Object.keys(selected).filter(k => selected[k]);

  const uploadFile = async (file) => {
    if (!file) return;
    toast('Uploading…');
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const r = await api('/api/uploads', { method:'POST', body:{ name:file.name, mime:file.type, base64: reader.result }});
        setMedia({ url: r.url, type: file.type.startsWith('video/') ? 'video' : 'image' });
        toast('✓ Uploaded');
      } catch(e) { toast(e.message,'error'); }
    };
    reader.readAsDataURL(file);
  };

  // UGC-style prompt prepended when generating for AD mode — forces native
  // organic look. Server-side ugcify() catches anything that slips through.
  const visualPrompt = () => {
    if (mode === 'ad') {
      return `${scope || 'homeowner with a service problem'} — iPhone photo, natural light, unstaged, authentic, slight grain, no logos, real environment`;
    }
    return scope;
  };

  const aiImage = async () => {
    if (!scope.trim()) return toast('Describe the image in "What are you posting about?" first','error');
    setGenBusy(true); setBillingWarning(null);
    try {
      const r = await api('/api/marketing/creative/image', { method:'POST', body:{ prompt: visualPrompt(), aspect: mode === 'ad' ? '4:5' : '1:1' }});
      if (r.warning) setBillingWarning({ message: r.warning, url: r.billingUrl });
      if (r.url) { setMedia({ url: r.url, type: 'image' }); toast(r.warning ? '⚠ Using placeholder — add Replicate credit' : '✓ Image generated', r.warning ? 'warn' : 'success'); }
    } catch(e) { toast(e.message,'error'); }
    finally { setGenBusy(false); }
  };

  const aiVideo = async () => {
    if (!scope.trim() && !media) return toast('Describe the video or upload a still first','error');
    setGenBusy(true); setBillingWarning(null);
    try {
      // Long-form (>20s) routes to the slideshow + voiceover pipeline (up to 3 min).
      // Short clips use the Replicate i2v / t2v models (5-6s native).
      if (videoDuration > 20) {
        toast('Building 1-3 minute video — this takes ~2 minutes…');
        const r = await api('/api/marketing/creative/longform-video', { method:'POST', body:{
          topic: scope, targetSeconds: videoDuration,
        }});
        if (r.url) { setMedia({ url: r.url, type: 'video' }); toast(`✓ ${Math.round(r.seconds)}-sec video ready (${r.scenes} scenes)`); }
      } else {
        const r = await api('/api/marketing/creative/video', { method:'POST', body:{
          prompt: visualPrompt(), aspect: '9:16', tier: videoTier,
          imageUrl: media?.type === 'image' ? media.url : undefined,
        }});
        if (r.warning) setBillingWarning({ message: r.warning, url: r.billingUrl });
        if (r.url) { setMedia({ url: r.url, type: 'video' }); toast(r.warning ? '⚠ Using placeholder — add Replicate credit' : `✓ ${videoTier==='premium'?'Premium ':''}Video generated`, r.warning ? 'warn' : 'success'); }
      }
    } catch(e) {
      if (e.code === 'CREDIT_INSUFFICIENT' || /not enough ai credit/i.test(e.message)) {
        toast('Not enough AI credit — top up in Billing → AI Credits', 'error');
      } else toast(e.message, 'error');
    }
    finally { setGenBusy(false); }
  };

  const aiWrite = async () => {
    if (!scope.trim()) return toast('Tell the AI what to write about','error');
    setAiBusy(true);
    try {
      // Ad mode → Schwartz/Ogilvy ad copy. Organic → conversational social caption.
      const endpoint = mode === 'ad' ? '/api/marketing/ad-copy-draft' : '/api/marketing/caption-draft';
      const r = await api(endpoint, { method:'POST', body:{ scope, mode }});
      if (r.text) {
        setCaption(r.text);
        toast('✓ Draft written');
      } else if (r.error) {
        toast(r.error, 'error');
      } else {
        toast('No draft returned — is ANTHROPIC_API_KEY set?', 'error');
      }
    } catch(e) { toast(e.message,'error'); }
    finally { setAiBusy(false); }
  };

  const post = async () => {
    if (!selectedKeys.length) return toast('Pick at least one channel','error');
    if (!caption.trim() && !media) return toast('Add a caption or media','error');
    setPosting(true);
    setResults(null);

    // Scheduled — queue server-side instead of immediate publish.
    if (scheduleAt) {
      try {
        await api('/api/social/schedule', { method:'POST', body:{
          platforms: selectedKeys, caption,
          imageUrl: media?.type === 'image' ? media.url : undefined,
          videoUrl: media?.type === 'video' ? media.url : undefined,
          scheduledFor: new Date(scheduleAt).toISOString(),
        }});
        toast(`📅 Scheduled for ${new Date(scheduleAt).toLocaleString()}`);
        setCaption(''); setMedia(null); setSelected({}); setScheduleAt('');
      } catch (e) { toast(e.message, 'error'); }
      setPosting(false); return;
    }

    const out = [];
    for (const platform of selectedKeys) {
      try {
        const r = await api('/api/social/post', {
          method:'POST',
          body: {
            platform, caption,
            imageUrl: media?.type === 'image' ? media.url : undefined,
            videoUrl: media?.type === 'video' ? media.url : undefined,
          },
        });
        out.push({ platform, ok: true, ...r });
      } catch(e) { out.push({ platform, ok: false, error: e.message }); }
    }
    setResults(out);
    setPosting(false);
    const ok = out.filter(x=>x.ok).length;
    if (ok === out.length) toast(`✅ Posted to ${ok} channel${ok===1?'':'s'}`);
    else toast(`Posted to ${ok}/${out.length}`, ok ? 'success' : 'error');
  };

  const segs = Math.max(1, caption.length);
  return <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:18 }}>
    <div>
      {/* Mode toggle — organic post vs paid ad. Drives prompt + visual style. */}
      <div style={{ ...styles.card, padding:6, marginBottom:14, display:'flex', gap:4 }}>
        {[
          { id:'organic', label:'🌱 Organic Post', desc:'Casual social caption' },
          { id:'ad',      label:'📣 Paid Ad',     desc:'Schwartz + Ogilvy + UGC visual' },
        ].map(m => (
          <button key={m.id} onClick={()=>setMode(m.id)} style={{
            flex:1, padding:'10px 14px', borderRadius:8, border:'none', cursor:'pointer',
            background: mode === m.id ? G.purpleDim : 'transparent',
            color: mode === m.id ? G.purple : G.textMuted, fontWeight: mode === m.id ? 700 : 500, textAlign:'left',
          }}>
            <div style={{ fontSize:13 }}>{m.label}</div>
            <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{m.desc}</div>
          </button>
        ))}
      </div>

      {billingWarning && <div style={{ ...styles.card, padding:12, marginBottom:12, background:G.yellow+'22', borderColor:G.yellow+'66' }}>
        <strong style={{ color:G.yellow, fontSize:13 }}>⚠ Replicate balance is empty</strong>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>
          Using a placeholder image. <a href={billingWarning.url || 'https://replicate.com/account/billing'} target="_blank" rel="noreferrer" style={{ color:G.cyan }}>Top up at replicate.com/account/billing →</a>
        </div>
      </div>}

      <div style={styles.card}>
        <h3 style={{ margin:'0 0 12px 0', fontSize:15 }}>
          {mode === 'ad' ? 'What ad are you running?' : 'What are you posting about?'}
        </h3>
        <Textarea value={scope} onChange={e=>setScope(e.target.value)}
          placeholder="e.g. 'Spring AC tune-up special, $89 — push the urgency angle.' Claude writes the caption from this."
          style={{ minHeight:60, marginBottom:8 }}/>
        <Btn sz="sm" v="primary" onClick={aiWrite} disabled={aiBusy || !scope.trim()}>
          {aiBusy ? <Spinner sz={14}/> : '🤖 Write caption with AI'}
        </Btn>
      </div>

      <div style={{ ...styles.card, marginTop:14 }}>
        <h3 style={{ margin:'0 0 12px 0', fontSize:15 }}>Caption</h3>
        <Textarea value={caption} onChange={e=>setCaption(e.target.value)} placeholder="Write your caption — or generate above…" style={{ minHeight:180, fontSize:14 }}/>
        <div style={{ fontSize:11, color:G.textMuted, marginTop:6 }}>{caption.length} chars</div>
      </div>

      <div style={{ ...styles.card, marginTop:14 }}>
        <h3 style={{ margin:'0 0 12px 0', fontSize:15 }}>Media (optional)</h3>
        {media ? (
          <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
            {media.type === 'image'
              ? <img src={media.url} style={{ width:200, height:200, objectFit:'cover', borderRadius:10 }}/>
              : <video src={media.url} controls style={{ width:200, height:200, borderRadius:10, background:'#000' }}/>}
            <div>
              <Btn sz="sm" v="danger" onClick={()=>setMedia(null)}>Remove</Btn>
            </div>
          </div>
        ) : (
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
            <label style={{ ...styles.card, padding:'12px 16px', cursor:'pointer', fontSize:13 }}>
              🖼️ Upload image
              <input type="file" accept="image/*" style={{ display:'none' }} onChange={e=>uploadFile(e.target.files?.[0])}/>
            </label>
            <label style={{ ...styles.card, padding:'12px 16px', cursor:'pointer', fontSize:13 }}>
              🎬 Upload video
              <input type="file" accept="video/*" style={{ display:'none' }} onChange={e=>uploadFile(e.target.files?.[0])}/>
            </label>
            <Btn sz="sm" onClick={aiImage} disabled={genBusy || !scope.trim()}>
              {genBusy ? <Spinner sz={14}/> : '🤖 AI image'}
            </Btn>
            <Btn sz="sm" onClick={aiVideo} disabled={genBusy}>
              {genBusy ? <Spinner sz={14}/> : '🎥 AI video'}
            </Btn>
            <Select value={videoDuration} onChange={e=>setVideoDuration(Number(e.target.value))} style={{ width:140 }}>
              <option value={6}>6 sec clip</option>
              <option value={30}>30 sec</option>
              <option value={60}>1 minute</option>
              <option value={90}>1.5 minutes</option>
              <option value={120}>2 minutes</option>
              <option value={180}>3 minutes</option>
            </Select>
            {videoDuration <= 20 && (
              <Select value={videoTier} onChange={e=>setVideoTier(e.target.value)} style={{ width:160 }}>
                <option value="standard">Standard — $0.07</option>
                <option value="hd">HD — $0.11</option>
                <option value="premium">Premium (Higgsfield) — $0.98</option>
              </Select>
            )}
            {videoDuration > 20 && (
              <span style={{ fontSize:11, color:G.textMuted }}>Slideshow + voiceover · ~${(0.30 + 0.05 * videoDuration/60).toFixed(2)}</span>
            )}
          </div>
        )}
      </div>

      {results && (
        <div style={{ ...styles.card, marginTop:14 }}>
          <h3 style={{ margin:'0 0 10px', fontSize:15 }}>📊 Results</h3>
          {results.map((r, i) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${G.cardBorder}55` }}>
              <span>{PLATFORM_GUIDE[r.platform]?.label || r.platform}</span>
              <span style={{ color: r.ok ? G.green : G.red, fontWeight:600 }}>
                {r.ok ? `✓ Posted` : `✗ ${r.error || 'failed'}`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Right rail: channels + post button */}
    <div>
      <div style={{ ...styles.card, position:'sticky', top:20 }}>
        <h3 style={{ margin:'0 0 12px 0', fontSize:15 }}>Channels</h3>
        {platforms.map(p => {
          const disabled = !p.available || (p.requiresVideo && media?.type !== 'video');
          return (
            <div key={p.key} onClick={()=>!disabled && toggle(p.key)} style={{
              padding:'10px 12px', borderRadius:8, cursor: disabled?'not-allowed':'pointer',
              background: selected[p.key] ? G.purpleDim : G.input,
              border: `2px solid ${selected[p.key] ? G.purple : 'transparent'}`,
              opacity: disabled ? 0.4 : 1, marginBottom:6,
            }}>
              <div style={{ fontSize:13, fontWeight:600 }}>{p.label}</div>
              <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>
                {!p.available ? 'Not connected' :
                  p.requiresVideo && media?.type !== 'video' ? 'Needs video' :
                  selected[p.key] ? '✓ Selected' : p.best}
              </div>
            </div>
          );
        })}
        {/* Schedule control — leave blank to post now */}
        <div style={{ ...styles.card, padding:10, marginTop:10, background:G.input }}>
          <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>📅 Schedule (optional)</div>
          <input type="datetime-local" value={scheduleAt} onChange={e=>setScheduleAt(e.target.value)}
            style={{ width:'100%', padding:8, background:G.bg, color:G.text, border:`1px solid ${G.cardBorder}`, borderRadius:6, fontSize:13 }}/>
          {scheduleAt && <button onClick={()=>setScheduleAt('')} style={{ background:'none', border:'none', color:G.textMuted, fontSize:11, cursor:'pointer', marginTop:4 }}>Clear · post now</button>}
        </div>

        <Btn v="primary" full onClick={post} disabled={posting || !selectedKeys.length}
          style={{ marginTop:14, padding:'14px', fontSize:14 }}>
          {posting ? <Spinner sz={14}/> :
            scheduleAt ? `📅 Schedule for ${selectedKeys.length} channel${selectedKeys.length===1?'':'s'}` :
            `📲 Post to ${selectedKeys.length || 0} channel${selectedKeys.length===1?'':'s'}`}
        </Btn>
      </div>
    </div>
  </div>;
}

function AnglesTab({ toast }) {
  const copy = (t) => { try { navigator.clipboard.writeText(t); toast('Copied'); } catch {} };
  return <div>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:18, lineHeight:1.6 }}>
      Eugene Schwartz's 6-angle framework, adapted for trades. Pick the angle that matches the customer's current pain stage — copy a hook and a body, edit to your business, post.
    </div>
    {ANGLE_LIBRARY.map(a => (
      <div key={a.angle} style={{ ...styles.card, marginBottom:14, padding:18 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
          <span style={{ fontSize:24 }}>{a.emoji}</span>
          <div>
            <h3 style={{ margin:0, fontSize:16 }}>{a.angle}</h3>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{a.description}</div>
          </div>
        </div>
        <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>Hooks</div>
        {a.hooks.map((h, i) => (
          <div key={i} onClick={()=>copy(h)} style={{ padding:'10px 12px', background:G.input, borderRadius:6, marginBottom:6, cursor:'pointer', fontSize:13, lineHeight:1.5 }}>
            "{h}" <span style={{ float:'right', fontSize:10, color:G.textMuted }}>📋 click to copy</span>
          </div>
        ))}
        <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginTop:12, marginBottom:6 }}>Body</div>
        {a.body.map((b, i) => (
          <div key={i} onClick={()=>copy(b)} style={{ padding:'10px 12px', background:G.input, borderRadius:6, marginBottom:6, cursor:'pointer', fontSize:13, lineHeight:1.5 }}>
            {b}
          </div>
        ))}
      </div>
    ))}
  </div>;
}

// Scheduled posts + the approval queue for AI-drip-generated drafts.
function ScheduledTab({ toast }) {
  const [items, setItems] = useState(null);
  const load = () => api('/api/social/scheduled').then(setItems).catch(()=>setItems([]));
  // Inline arrow so React doesn't store the returned Promise as cleanup fn.
  useEffect(() => { load(); }, []);
  if (items === null) return <Spinner sz={24}/>;
  const pending = items.filter(i => i.status === 'pending');
  const needsApproval = pending.filter(i => i.needsApproval);
  const readyToFire  = pending.filter(i => !i.needsApproval);
  const past = items.filter(i => i.status !== 'pending');

  const approve = async (id) => { try { await api(`/api/social/scheduled/${id}/approve`, { method:'POST' }); load(); toast('✓ Approved — will publish at scheduled time'); } catch(e) { toast(e.message,'error'); } };
  const remove  = async (id) => { if (!confirm('Delete this scheduled post?')) return; try { await api(`/api/social/scheduled/${id}`, { method:'DELETE' }); load(); toast('Deleted'); } catch(e) { toast(e.message,'error'); } };

  const renderItem = (i, opts={}) => (
    <div key={i.id} style={{ ...styles.card, padding:14, marginBottom:8 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:14 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>
            {new Date(i.scheduledFor).toLocaleString()} · {i.platforms.join(', ')}
            {i.recurringId && <span style={{ marginLeft:8, color:G.purple }}>🤖 AI drip</span>}
          </div>
          <div style={{ fontSize:13, whiteSpace:'pre-wrap', maxHeight:120, overflow:'hidden' }}>{i.caption}</div>
          {i.imageUrl && <img src={i.imageUrl} style={{ width:80, height:80, objectFit:'cover', borderRadius:6, marginTop:8 }}/>}
          {i.error && <div style={{ fontSize:12, color:G.red, marginTop:6 }}>⚠ {i.error}</div>}
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {opts.canApprove && <Btn sz="xs" v="primary" onClick={()=>approve(i.id)}>✓ Approve</Btn>}
          <Btn sz="xs" v="danger" onClick={()=>remove(i.id)}>× Delete</Btn>
        </div>
      </div>
    </div>
  );

  return <div>
    {needsApproval.length > 0 && <>
      <h3 style={{ margin:'0 0 10px', fontSize:15, color:G.yellow }}>🤖 AI Drip Awaiting Approval ({needsApproval.length})</h3>
      <div style={{ marginBottom:18 }}>{needsApproval.map(i => renderItem(i, { canApprove:true }))}</div>
    </>}
    <h3 style={{ margin:'0 0 10px', fontSize:15 }}>📅 Scheduled ({readyToFire.length})</h3>
    {readyToFire.length === 0 ? <div style={{ ...styles.card, padding:30, textAlign:'center', color:G.textMuted }}>Nothing scheduled. Use Compose → pick a future time to schedule a post.</div>
      : readyToFire.map(i => renderItem(i))}
    {past.length > 0 && <>
      <h3 style={{ margin:'18px 0 10px', fontSize:15, color:G.textMuted }}>📋 Past ({past.length})</h3>
      {past.slice(0,10).map(i => renderItem(i))}
    </>}
  </div>;
}

// Recurring AI drip — owner defines a topic + cadence; system queues a draft
// on schedule for review.
function RecurringTab({ toast }) {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState(null);
  const load = () => api('/api/social/recurring').then(setItems).catch(()=>setItems([]));
  useEffect(() => { load(); api('/api/social/status').then(setStatus).catch(()=>{}); }, []);

  const togglePaused = async (i) => { try { await api(`/api/social/recurring/${i.id}`, { method:'PATCH', body:{ paused: !i.paused }}); load(); toast(i.paused ? 'Resumed' : 'Paused'); } catch(e) { toast(e.message,'error'); } };
  const toggleAuto   = async (i) => { try { await api(`/api/social/recurring/${i.id}`, { method:'PATCH', body:{ autoPublish: !i.autoPublish }}); load(); toast(i.autoPublish ? 'Now requires approval' : 'Auto-publish ON'); } catch(e) { toast(e.message,'error'); } };
  const remove       = async (i) => { if (!confirm(`Delete drip "${i.name}"?`)) return; try { await api(`/api/social/recurring/${i.id}`, { method:'DELETE' }); load(); toast('Deleted'); } catch(e) { toast(e.message,'error'); } };

  if (items === null) return <Spinner sz={24}/>;

  return <div>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
      <div>
        <h3 style={{ margin:0, fontSize:15 }}>🤖 AI Drip — auto-generate posts on a cadence</h3>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>Each drip auto-writes a fresh caption on its schedule. You approve before it publishes (or flip auto-publish ON).</div>
      </div>
      <Btn v="primary" onClick={()=>setCreating(true)}>+ New Drip</Btn>
    </div>

    {items.length === 0 ? <div style={{ ...styles.card, padding:40, textAlign:'center', color:G.textMuted }}>No drips yet. Click "+ New Drip" to set one up.</div>
      : items.map(i => (
        <div key={i.id} style={{ ...styles.card, padding:14, marginBottom:10 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:14 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:600, fontSize:14 }}>
                {i.name} {i.paused && <Badge c={G.textMuted} bg={G.input}>paused</Badge>} {i.autoPublish ? <Badge c={G.purple} bg={G.purpleDim}>auto-publish</Badge> : <Badge c={G.yellow} bg={G.yellow+'22'}>needs approval</Badge>}
              </div>
              <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>
                Cadence: <strong>{i.cadence}</strong> · Mode: {i.mode === 'ad' ? '📣 Ad' : '🌱 Organic'} · Channels: {i.platforms.join(', ')}
              </div>
              <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>Scope: "{i.scope}"</div>
              <div style={{ fontSize:11, color:G.textMuted, marginTop:4 }}>
                {i.lastRunAt ? `Last run ${new Date(i.lastRunAt).toLocaleString()} · ` : ''}
                Next run {i.nextRunAt ? new Date(i.nextRunAt).toLocaleString() : '—'}
              </div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              <Btn sz="xs" onClick={()=>togglePaused(i)}>{i.paused ? '▶ Resume' : '⏸ Pause'}</Btn>
              <Btn sz="xs" onClick={()=>toggleAuto(i)}>{i.autoPublish ? 'Require approval' : 'Auto-publish'}</Btn>
              <Btn sz="xs" v="danger" onClick={()=>remove(i)}>× Delete</Btn>
            </div>
          </div>
        </div>
      ))
    }

    {creating && <NewDripModal status={status} onClose={()=>setCreating(false)} onCreated={()=>{ setCreating(false); load(); toast('✓ Drip created'); }} toast={toast}/>}
  </div>;
}

function NewDripModal({ status, onClose, onCreated, toast }) {
  const [f, setF] = useState({ name:'', cadence:'weekly', scope:'', platforms:[], mode:'organic', autoPublish:false });
  const [busy, setBusy] = useState(false);
  const allPlatforms = [
    { k:'facebook_page',      label:'Facebook Page',      available: status?.facebook?.connected },
    { k:'instagram_business', label:'Instagram',          available: status?.instagram?.connected },
    { k:'gbp',                label:'Google Business',    available: status?.gbp?.connected },
  ];
  const toggle = (k) => setF(s => ({ ...s, platforms: s.platforms.includes(k) ? s.platforms.filter(x=>x!==k) : [...s.platforms, k] }));
  const submit = async () => {
    if (!f.scope.trim()) return toast('Tell the AI what topic / angle', 'error');
    if (!f.platforms.length) return toast('Pick at least one channel', 'error');
    setBusy(true);
    try { await api('/api/social/recurring', { method:'POST', body: f }); onCreated(); }
    catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <Modal open onClose={onClose} width={520}>
    <h2 style={{ margin:'0 0 14px 0', fontSize:18 }}>🤖 New AI Drip</h2>
    <Field label="Name (internal)"><Input value={f.name} onChange={e=>setF({...f, name:e.target.value})} placeholder="e.g. 'Weekly maintenance tip'"/></Field>
    <Field label="Topic / angle the AI should write about" hint="Stays the same; the AI varies the wording each run.">
      <Textarea value={f.scope} onChange={e=>setF({...f, scope:e.target.value})} placeholder="e.g. 'Spring AC tune-up reminder for Tampa homeowners — emphasize beating the May rush'" style={{ minHeight:70 }}/>
    </Field>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Cadence">
        <Select value={f.cadence} onChange={e=>setF({...f, cadence:e.target.value})}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="biweekly">Every 2 weeks</option>
          <option value="monthly">Monthly</option>
        </Select>
      </Field>
      <Field label="Mode">
        <Select value={f.mode} onChange={e=>setF({...f, mode:e.target.value})}>
          <option value="organic">🌱 Organic post</option>
          <option value="ad">📣 Ad copy (Schwartz/Ogilvy)</option>
        </Select>
      </Field>
    </div>
    <Field label="Channels">
      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
        {allPlatforms.map(p => (
          <button key={p.k} onClick={()=>p.available && toggle(p.k)} disabled={!p.available}
            style={{ padding:'6px 12px', borderRadius:14, fontSize:12, fontWeight:600,
              border: `1px solid ${f.platforms.includes(p.k) ? G.purple : G.cardBorder}`,
              background: f.platforms.includes(p.k) ? G.purpleDim : 'transparent',
              color: f.platforms.includes(p.k) ? G.purple : (p.available ? G.text : G.textMuted),
              cursor: p.available ? 'pointer' : 'not-allowed', opacity: p.available ? 1 : 0.4 }}>
            {p.label}{!p.available ? ' (not connected)' : ''}
          </button>
        ))}
      </div>
    </Field>
    <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:10, fontSize:13, cursor:'pointer' }}>
      <input type="checkbox" checked={f.autoPublish} onChange={e=>setF({...f, autoPublish:e.target.checked})}/>
      Auto-publish without approval (otherwise drafts wait in the Scheduled tab)
    </label>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
      <Btn v="primary" onClick={submit} disabled={busy || !f.scope.trim() || !f.platforms.length}>{busy ? <Spinner sz={14}/> : '🤖 Create Drip'}</Btn>
    </div>
  </Modal>;
}

function HistoryTab({ toast }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api('/api/social/posts').then(r=>setPosts(r.posts||[])).catch(()=>{}).finally(()=>setLoading(false)); }, []);
  if (loading) return <Spinner sz={32}/>;
  if (!posts.length) return <div style={{ ...styles.card, padding:40, textAlign:'center', color:G.textMuted }}>No posts yet. Go to Compose to publish your first.</div>;
  return <table style={{ width:'100%' }}>
    <thead><tr>{['Posted','Channel','Caption','Status'].map(h=><th key={h} style={styles.th}>{h}</th>)}</tr></thead>
    <tbody>{posts.map(p => (
      <tr key={p.id}>
        <td style={styles.td}>{new Date(p.posted_at || p.created_at).toLocaleString()}</td>
        <td style={styles.td}>{PLATFORM_GUIDE[p.platform]?.label || p.platform}</td>
        <td style={{ ...styles.td, maxWidth:400, overflow:'hidden', textOverflow:'ellipsis' }}>{(p.caption||'').slice(0,100)}</td>
        <td style={styles.td}>
          <Badge c={p.status==='posted'?G.green:p.status==='failed'?G.red:G.yellow} bg={(p.status==='posted'?G.green:p.status==='failed'?G.red:G.yellow)+'22'}>
            {p.status}
          </Badge>
        </td>
      </tr>
    ))}</tbody>
  </table>;
}
